import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Spinner,
  Text,
  Flex,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import React, { useEffect, useState } from "react";
import {
  useGetAppointmentFamilyHistory,
  useGetAppointmentSocialHistory,
  useGetAppointmentPastMedicalHistory,
  useGetAppointmentMedicationHistory,
  useDeleteSingleMedication,
} from "../../../apis/patients";
import ConfirmationDialog from "../../../components/ConfirmationDialog.tsx";
import colors from "../../../utils/colors";

const PatientOverview = ({ appointmentId }) => {
  const [patientData, setPatientData] = useState({
    "Past Medical History": [],
    "Family History": [],
    "Social History": [],
    "Medication History": [],
  });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedMedication, setSelectedMedication] = useState<any>(null);

  const { mutate: deleteMedication, isPending: deletingMedication } =
    useDeleteSingleMedication();

  const { data: pastMedicalHistory, isLoading: loadingPastMedical } =
    useGetAppointmentPastMedicalHistory(appointmentId);

  const { data: familyHistory, isLoading: loadingFamily } =
    useGetAppointmentFamilyHistory(appointmentId);

  const { data: socialHistory, isLoading: loadingSocial } =
    useGetAppointmentSocialHistory(appointmentId);

  const { data: medicationHistory, isLoading: loadingMedication } =
    useGetAppointmentMedicationHistory(appointmentId);

  useEffect(() => {
    const flattenHistories = (histories, key) =>
      histories?.flatMap((item) => item[key] || []) || [];

    setPatientData({
      "Past Medical History": flattenHistories(
        pastMedicalHistory,
        "pastMedicalHistories",
      ),
      "Family History": flattenHistories(familyHistory, "familyHistories"),
      "Social History": flattenHistories(socialHistory, "socialHistories"),
      "Medication History": medicationHistory?.medications || [],
    });
  }, [pastMedicalHistory, familyHistory, socialHistory, medicationHistory]);

  const handleDeleteClick = (medication: any) => {
    setSelectedMedication(medication);
    onOpen();
  };

  const handleConfirmDelete = () => {
    if (!selectedMedication || !medicationHistory) return;

    deleteMedication(
      {
        medicationHistoryId: medicationHistory._id,
        medicationObjectId: selectedMedication._id,
        appointmentId,
        patientId: medicationHistory.patientId,
      },
      {
        onSuccess: () => {
          setSelectedMedication(null);
          onClose();
        },
      },
    );
  };

  const renderSectionContent = (section) => {
    const data = patientData[section];

    const isLoading =
      (section === "Past Medical History" && loadingPastMedical) ||
      (section === "Family History" && loadingFamily) ||
      (section === "Social History" && loadingSocial) ||
      (section === "Medication History" && loadingMedication);

    if (isLoading) {
      return <Spinner color="teal.500" />;
    }

    if (!data.length) {
      return (
        <Text color="gray.500" fontStyle="italic">
          No data available for {section.toLowerCase()}.
        </Text>
      );
    }

    return data.map((item, index) => {
      switch (section) {
        case "Past Medical History":
          return (
            <Box key={index} mb="10px">
              <Text fontWeight="bold">Category: {item.category || "N/A"}</Text>
              <Text>Notes: {item.notes || "N/A"}</Text>
            </Box>
          );

        case "Family History":
          return (
            <Box key={index} mb="10px">
              <Text fontWeight="bold">Category: {item.category || "N/A"}</Text>
              <Text>
                Family Member Affected: {item.familyMemberAffected || "N/A"}
              </Text>
              <Text>Notes: {item.notes || "N/A"}</Text>
            </Box>
          );

        case "Social History":
          return (
            <Box key={index} mb="10px">
              <Text fontWeight="bold">Habits: {item.habits || "N/A"}</Text>
              <Text>Notes: {item.notes || "N/A"}</Text>
            </Box>
          );

        case "Medication History":
          return (
            <Flex
              key={item._id}
              justify="space-between"
              align="flex-start"
              mb="12px"
              p="10px"
              borderWidth="1px"
              borderRadius="md"
            >
              <Box flex="1">
                <Text fontWeight="bold">
                  Drug: {item.route || "N/A"} {item.name || "N/A"}{" "}
                  {item.dosage || "N/A"} {item.frequency || "N/A"}
                </Text>

                <Text mt={1}>Notes: {item.notes || "N/A"}</Text>
              </Box>

              <IconButton
                aria-label="Delete Medication"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDeleteClick(item)}
              />
            </Flex>
          );

        default:
          return null;
      }
    });
  };

  return (
    <Box>
      {Object.keys(patientData).map((section) => (
        <Accordion
          allowToggle
          key={section}
          mb="12px"
          borderRadius="8px"
          backgroundColor="white"
          boxShadow="md"
        >
          <AccordionItem
            fontFamily="Lato"
            borderRadius="8px"
            background="transparent"
            border="none"
          >
            <AccordionButton p="10px 20px">
              <Box
                as="span"
                flex="1"
                textAlign="left"
                fontSize="16px"
                fontWeight={500}
                color={colors.dark}
              >
                {section}
              </Box>

              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel
              p="20px"
              fontWeight={400}
              fontSize="14px"
              color={colors.secondaryBlack}
            >
              {renderSectionContent(section)}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ))}

      <ConfirmationDialog
        isOpen={isOpen}
        onClose={() => {
          setSelectedMedication(null);
          onClose();
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Medication"
        message={
          <>
            <Text>Are you sure you want to delete this medication?</Text>

            <Text mt={3} fontWeight="bold">
              {selectedMedication?.route} {selectedMedication?.name}{" "}
              {selectedMedication?.dosage}
            </Text>

            <Text mt={3}>This action cannot be undone.</Text>
          </>
        }
        confirmText="Delete"
        confirmColorScheme="red"
        isLoading={deletingMedication}
      />
    </Box>
  );
};

export default PatientOverview;
